import React, { useState, useEffect } from 'react';
import { colors } from './TabNavigation';

function Landing({ onNavigate }) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    // Coalesce bursts of resize events into one state update per frame.
    let raf = 0;
    const handle = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWindowWidth(window.innerWidth));
    };
    window.addEventListener('resize', handle);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', handle); };
  }, []);
  const isMobile = windowWidth < 700;

  const sectionTitle = {
    fontSize: isMobile ? '12px' : '10px',
    fontWeight: '500',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: colors.gold,
    marginBottom: '24px',
  };

  const features = [
    {
      tab: 'calculator',
      symbol: '✦',
      title: 'Birth Chart',
      subtitle: 'Your permanent cosmic map',
      description:
        'The sky at the exact moment you were born, calculated and explained. It shows who you are by nature — your core identity, emotional world, and how you show up to others. Every planet has a placement, and each placement has a meaning. Tap anything to understand it in plain English.',
      cta: 'Calculate your chart',
    },
    {
      tab: 'calendar',
      symbol: '◑',
      title: "Today's Sky",
      subtitle: 'The sky right now, personalised to your chart',
      description:
        "Each day the planets move into new positions and activate different parts of your chart. Today's Sky shows you which energies are live in your life right now — and translates them into something useful: what today calls for, what to lean into, and what to avoid.",
      cta: "See today's sky",
    },
    {
      tab: 'year-calendar',
      symbol: '◎',
      title: 'Transit Calendar',
      subtitle: 'The year ahead',
      description:
        'Every major planetary event across the year — sign changes, retrogrades, eclipses, conjunctions — laid out month by month. Useful for understanding why certain periods feel heavier or lighter, and for timing things that matter.',
      cta: 'Open the calendar',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Enter your birth details',
      body: 'Date, time, and city. That\'s all we need.',
    },
    {
      number: '02',
      title: 'We calculate your chart',
      body: 'Using Swiss Ephemeris — the same engine professional astrologers use.',
    },
    {
      number: '03',
      title: 'Explore what it means',
      body: 'Tap any planet or aspect to get a plain-English explanation. No jargon.',
    },
  ];

  return (
    <div>

      {/* ── Value prop ── */}
      <section style={{
        maxWidth: '640px',
        margin: '0 auto',
        padding: '0 24px 48px',
        textAlign: 'center',
        borderBottom: `1px solid ${colors.borderSubtle}`,
      }}>
        <h2 style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 'clamp(22px, 7vw, 38px)',
          fontWeight: '400',
          color: colors.textPrimary,
          lineHeight: '1.2',
          letterSpacing: '-0.3px',
          marginBottom: '20px',
          marginTop: 0,
        }}>
          Astrology is complex.<br />We made it simple.
        </h2>
        <p style={{
          fontSize: isMobile ? '17px' : '16px',
          fontWeight: '300',
          fontStyle: 'italic',
          color: colors.textSecondary,
          lineHeight: '1.8',
          margin: '0 0 36px',
        }}>
          We turn complex astrology into simple, personalized insights — so you can understand yourself, your patterns, and your path.
        </p>
        <button
          onClick={() => onNavigate('calculator')}
          style={{
            padding: '16px 40px',
            fontSize: isMobile ? '13px' : '11px',
            fontFamily: 'inherit',
            fontWeight: '500',
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: colors.darkBg,
            backgroundColor: colors.gold,
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.goldBright}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.gold}
        >
          Calculate your birth chart
        </button>
      </section>

      {/* ── Features ── */}
      <section style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '48px 24px',
        borderBottom: `1px solid ${colors.borderSubtle}`,
      }}>
        <p style={{ ...sectionTitle, textAlign: 'center', marginBottom: '48px' }}>
          What's inside
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1px',
          backgroundColor: colors.borderSubtle,
        }}>
          {features.map(({ tab, symbol, title, subtitle, description, cta }) => (
            <div
              key={tab}
              style={{
                backgroundColor: colors.creamBg,
                padding: '40px 32px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{
                fontSize: '24px',
                color: colors.gold,
                marginBottom: '20px',
                lineHeight: 1,
              }}>
                {symbol}
              </div>
              <h3 style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: '22px',
                fontWeight: '400',
                color: colors.textPrimary,
                margin: '0 0 4px',
              }}>
                {title}
              </h3>
              {subtitle && (
                <p style={{
                  fontSize: isMobile ? '14px' : '12px',
                  fontStyle: 'italic',
                  fontWeight: '300',
                  color: colors.gold,
                  margin: '0 0 16px',
                  letterSpacing: '0.2px',
                }}>
                  {subtitle}
                </p>
              )}
              <p style={{
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '300',
                color: colors.textSecondary,
                lineHeight: '1.75',
                margin: '0 0 28px',
                flex: 1,
              }}>
                {description}
              </p>
              <button
                onClick={() => onNavigate(tab)}
                style={{
                  background: 'none',
                  border: `1px solid ${colors.borderGold}`,
                  color: colors.gold,
                  fontSize: isMobile ? '12px' : '10px',
                  fontFamily: 'inherit',
                  fontWeight: '500',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  padding: '12px 20px',
                  cursor: 'pointer',
                  alignSelf: 'flex-start',
                  transition: 'border-color 0.2s, color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.gold;
                  e.currentTarget.style.color = colors.goldBright;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.borderGold;
                  e.currentTarget.style.color = colors.gold;
                }}
              >
                {cta} →
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '80px 24px',
        borderBottom: `1px solid ${colors.borderSubtle}`,
      }}>
        <p style={sectionTitle}>How it works</p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '48px',
          marginTop: '8px',
        }}>
          {steps.map(({ number, title, body }) => (
            <div key={number}>
              <div style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: '48px',
                fontWeight: '300',
                color: colors.borderGold,
                lineHeight: 1,
                marginBottom: '16px',
              }}>
                {number}
              </div>
              <h3 style={{
                fontSize: isMobile ? '17px' : '15px',
                fontWeight: '500',
                color: colors.textPrimary,
                margin: '0 0 8px',
                letterSpacing: '0.2px',
              }}>
                {title}
              </h3>
              <p style={{
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '300',
                color: colors.textSecondary,
                lineHeight: '1.7',
                margin: 0,
              }}>
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{
        maxWidth: '640px',
        margin: '0 auto',
        padding: '80px 24px 0',
        textAlign: 'center',
      }}>
        <p style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: '28px',
          fontWeight: '400',
          color: colors.textPrimary,
          margin: '0 0 28px',
          lineHeight: '1.3',
        }}>
          Ready to see your cosmic blueprint?
        </p>
        <button
          onClick={() => onNavigate('calculator')}
          style={{
            padding: '16px 40px',
            fontSize: isMobile ? '13px' : '11px',
            fontFamily: 'inherit',
            fontWeight: '500',
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: colors.darkBg,
            backgroundColor: colors.gold,
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.goldBright}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.gold}
        >
          Calculate your birth chart — it's free
        </button>
        <p style={{
          fontSize: isMobile ? '14px' : '12px',
          fontStyle: 'italic',
          color: colors.textMuted,
          marginTop: '16px',
        }}>
          No sign-up required
        </p>
      </section>

    </div>
  );
}

export default Landing;
